if (!customElements.get('rs7gt-collage')) {
  customElements.define('rs7gt-collage', class extends HTMLElement {
    connectedCallback() {
      if (this.controller && !this.controller.signal.aborted) return;
      this.viewport = this.querySelector('[data-collage-viewport]');
      this.track = this.querySelector('[data-collage-track]');
      if (!this.viewport || !this.track || !this.querySelector('[data-collage-sticky]')) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.launchMode = this.hasAttribute('data-launch-collage');
      this.launchRoot = this.closest('rs7gt-launch');
      this.launchScene = this.closest('.rs7-launch-photos');
      this.launchSteps = Array.from(this.querySelectorAll('[data-collage-step]'));
      this.launchProgress = this.querySelector('[data-collage-progress]');
      this.pinned = false;
      this.inView = true;
      this.offset = 0;
      this.photos = Array.from(this.track.children);
      this.copies = [];
      this.looping = false;
      this.loopActive = false;
      this.returning = false;
      this.bypassed = false;
      this.leaving = false;
      this.lastScrollY = window.scrollY;
      this.pinQuery = window.matchMedia('(min-width: 990px) and (min-height: 600px) and (prefers-reduced-motion: no-preference)');
      this.pinQuery.addEventListener('change', () => this.requestMeasure(), options);
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.motionQuery.addEventListener('change', () => this.requestMeasure(), options);
      window.addEventListener('resize', () => this.requestMeasure(), options);
      window.addEventListener('load', () => this.requestMeasure(), options);
      window.addEventListener('pageshow', () => this.requestMeasure(), options);
      window.addEventListener('scroll', () => this.onScroll(), { ...options, passive: true });
      window.addEventListener('scrollend', () => {
        if (!this.leaving) return;
        this.leaving = false;
        if (window.scrollY < this.start) this.bypassed = false;
      }, options);
      document.addEventListener('focusin', event => {
        if (this.looping && !this.returning && event.target !== document.body && !this.contains(event.target)) this.bypassed = true;
      }, options);
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.loopActive && !event.target.closest('input, textarea, select, [contenteditable]')) {
          event.preventDefault();
          this.returnToTop();
        }
      }, options);
      this.addEventListener('load', () => this.requestMeasure(), { ...options, capture: true });
      this.addEventListener('rs7gt:leave-collage', () => {
        this.bypassed = true;
        this.leaving = true;
        this.loopActive = false;
        this.returning = false;
      }, options);
      this.addEventListener('rs7gt:enter-collage', () => {
        this.bypassed = false;
        this.leaving = false;
        this.loopActive = false;
        this.returning = false;
      }, options);
      this.viewport.addEventListener('keydown', event => this.onKeydown(event), options);
      if (this.launchMode) {
        this.launchSteps.forEach(button => button.addEventListener('click', () => {
          const step = Number(button.dataset.collageStep);
          if (this.launchPinned) this.selectLaunchStep(this.launchPosition() + step);
          else this.scrollLaunch(this.viewport.scrollLeft + step * this.viewportWidth * 0.8);
        }, options));
        this.viewport.addEventListener('scroll', () => this.updateLaunchControls(), { ...options, passive: true });
        this.viewport.addEventListener('dragstart', event => event.preventDefault(), options);
        this.launchRoot?.addEventListener('rs7gt:motionchange', () => this.requestMeasure(), options);
        this.launchRoot?.addEventListener('rs7gt:pagecoveragechange', () => this.updateLayer(), options);
      }
      this.addEventListener('shopify:block:select', event => this.revealBlock(event), options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          cancelAnimationFrame(this.renderFrame);
          this.renderFrame = 0;
          this.track.style.removeProperty('will-change');
        } else {
          this.requestMeasure();
        }
      }, options);

      this.resizeObserver = new ResizeObserver(() => this.requestMeasure());
      this.resizeObserver.observe(this.track);
      this.resizeObserver.observe(this.viewport);
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.updateLayer();
        if ((this.pinned || this.launchPinned) && !document.hidden) this.render();
      }, { rootMargin: '100px 0px' });
      this.intersectionObserver.observe(this);

      if (document.fonts) {
        document.fonts.ready.then(() => {
          if (!options.signal.aborted) this.requestMeasure();
        });
        document.fonts.addEventListener('loadingdone', () => this.requestMeasure(), options);
      }
      this.measure();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      cancelAnimationFrame(this.measureFrame);
      cancelAnimationFrame(this.renderFrame);
      this.measureFrame = 0;
      this.renderFrame = 0;
      this.pinned = false;
      this.removeCopies();
      this.removeAttribute('data-pinned');
      this.style.removeProperty('--collage-travel');
      this.track?.style.removeProperty('transform');
      this.track?.style.removeProperty('will-change');
      this.launchProgress?.style.removeProperty('--collage-progress');
      this.launchPinned = false;
      this.launchScene?.removeAttribute('data-collage-scroll');
      this.launchScene?.style.removeProperty('--collage-launch-pages');
    }

    requestMeasure() {
      if (this.measureFrame || !this.isConnected || !this.controller || this.controller.signal.aborted) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = 0;
        this.measure();
      });
    }

    measure(notify = true) {
      const wasPinned = this.pinned;
      const previousStart = this.start;
      const previousTravel = this.travel;
      this.viewportWidth = this.viewport.clientWidth;
      const trackBox = this.track.getBoundingClientRect();
      const photoBoxes = this.photos.map(photo => photo.getBoundingClientRect());
      const trackStyle = getComputedStyle(this.track);
      const lastEdge = photoBoxes.length ? Math.max(...photoBoxes.map(box => box.right)) : trackBox.left;
      const firstEdge = photoBoxes.length ? Math.min(...photoBoxes.map(box => box.left)) : trackBox.left;
      const originalWidth = lastEdge - trackBox.left + (parseFloat(trackStyle.paddingRight) || 0);
      this.cycleWidth = lastEdge - firstEdge + (parseFloat(trackStyle.columnGap) || 0);
      this.travel = Math.max(0, originalWidth - this.viewportWidth);
      if (this.launchMode) {
        this.measureLaunch(previousTravel, notify);
        return;
      }
      const story = this.closest('rs7gt-story');
      const hero = story?.querySelector('rs7gt-landing');
      const pageLoop = hero?.dataset.pageLoop === 'true' && hero.closest('.rs7gt-section') === story.firstElementChild && story.dataset.designMode !== 'true' && !this.motionQuery.matches;
      this.pinned = this.dataset.designMode !== 'true' && (pageLoop || (this.pinQuery.matches && this.travel > 1));
      this.looping = this.pinned && !pageLoop && this.dataset.infiniteScroll === 'true' && !this.hasAttribute('data-compact');
      if (this.looping) this.prepareCopies();
      else this.removeCopies();
      this.toggleAttribute('data-pinned', this.pinned);
      this.lastScrollY = window.scrollY;
      if (this.pinned) {
        this.scrollSpan = this.looping ? this.cycleWidth * 2 : this.travel;
        const distance = `${this.scrollSpan}px`;
        if (this.style.getPropertyValue('--collage-travel') !== distance) {
          this.style.setProperty('--collage-travel', distance);
        }
        if (!wasPinned) this.viewport.scrollLeft = 0;
        this.start = this.getBoundingClientRect().top + window.scrollY;
        this.render();
      } else {
        this.style.removeProperty('--collage-travel');
        this.track.style.removeProperty('transform');
        if (wasPinned) this.viewport.scrollLeft = this.offset;
      }
      this.updateLayer();
      this.lastScrollY = window.scrollY;
      if (wasPinned !== this.pinned || previousStart !== this.start || previousTravel !== this.travel) {
        this.dispatchEvent(new Event('rs7gt:collage-measure', { bubbles: true }));
      }
    }

    updateLayer() {
      const uncovered = !this.launchMode || (this.launchRoot?.sceneVisibility?.(this) ?? 1) > 0;
      if ((this.pinned || this.launchPinned) && this.inView && uncovered && !document.hidden) {
        this.track.style.willChange = 'transform';
      } else {
        this.track.style.removeProperty('will-change');
      }
    }

    requestRender() {
      if (!(this.pinned || this.launchPinned) || !this.inView || document.hidden || this.renderFrame) return;
      this.renderFrame = requestAnimationFrame(() => {
        this.renderFrame = 0;
        if (this.isConnected && (this.pinned || this.launchPinned) && this.inView && !document.hidden) this.render();
      });
    }

    render(y = window.scrollY) {
      if (this.launchMode) {
        if (!this.launchPinned) return;
        const progress = Math.max(0, Math.min(1, (y - this.launchStart) / this.launchSpan));
        this.offset = this.travel * progress;
        this.track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
        this.updateLaunchControls();
        return;
      }
      const distance = Math.max(0, y - this.start);
      if (this.looping) {
        if (distance > this.scrollSpan) {
          this.bypassed = true;
          this.loopActive = false;
        }
        if (this.returning || this.bypassed) return;
        this.loopActive = y >= this.start;
        this.offset = distance % this.cycleWidth;
        if (distance >= this.cycleWidth * 1.5) {
          const target = this.start + this.offset;
          this.lastScrollY = target;
          window.scrollTo({ top: target, behavior: 'instant' });
        }
      } else {
        this.offset = Math.min(this.travel, distance);
      }
      const transform = `translate3d(${-this.offset}px, 0px, 0px)`;
      if (this.track.style.transform !== transform) this.track.style.transform = transform;
    }

    onScroll() {
      if (this.launchMode) {
        this.requestRender();
        return;
      }
      const y = window.scrollY;
      const delta = y - this.lastScrollY;
      this.lastScrollY = y;
      if (this.measureFrame) return;
      if (this.looping) {
        if (this.returning) {
          if (y + window.innerHeight <= this.start + 1 || (delta > 1 && y < this.start)) {
            this.returning = false;
            this.loopActive = false;
          } else return;
        }
        if (this.leaving && delta < -0.5 && y < this.start) this.leaving = false;
        if (!this.leaving && y < this.start && (this.bypassed || !this.loopActive)) {
          this.bypassed = false;
          this.loopActive = false;
        }
        if (this.loopActive && !this.bypassed && delta < -0.5) {
          this.returnToTop();
          return;
        }
      }
      this.requestRender();
    }

    prepareCopies() {
      if (this.launchMode || !this.photos.length || !(this.cycleWidth > 0)) return;
      const repetitions = Math.ceil(this.viewportWidth / this.cycleWidth);
      if (this.copies.length === this.photos.length * repetitions) return;
      this.copies.forEach(copy => copy.remove());
      this.copies = [];
      const columns = Math.max(...this.photos.map(photo =>
        Number(photo.style.getPropertyValue('--collage-column')) + Number(photo.style.getPropertyValue('--collage-span')) - 1
      ));
      const fragment = document.createDocumentFragment();
      for (let repetition = 1; repetition <= repetitions; repetition++) {
        this.photos.forEach(photo => {
          const copy = photo.cloneNode(true);
          copy.setAttribute('aria-hidden', 'true');
          copy.inert = true;
          copy.removeAttribute('data-shopify-editor-block');
          copy.removeAttribute('id');
          copy.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
          copy.querySelectorAll('img').forEach(img => { img.alt = ''; });
          copy.style.setProperty('--collage-column', Number(photo.style.getPropertyValue('--collage-column')) + columns * repetition);
          this.copies.push(copy);
          fragment.append(copy);
        });
      }
      this.track.append(fragment);
    }

    removeCopies() {
      this.copies?.forEach(copy => copy.remove());
      this.copies = [];
      this.looping = false;
      this.loopActive = false;
      this.returning = false;
      this.bypassed = false;
      this.leaving = false;
    }

    returnToTop() {
      if (this.launchMode || this.returning) return;
      this.returning = true;
      this.loopActive = false;
      if (this.contains(document.activeElement)) document.querySelector('#MainContent')?.focus({ preventScroll: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    onKeydown(event) {
      if (event.target !== this.viewport || event.altKey || event.ctrlKey || event.metaKey) return;
      if (this.launchMode) {
        if (this.launchPinned && ['Home', 'End', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault();
          const step = event.key === 'Home' ? 0 : event.key === 'End' ? this.launchStepCount - 1
            : this.launchPosition() + (event.key === 'ArrowRight' ? 1 : -1);
          this.selectLaunchStep(step);
          return;
        }
        let target;
        if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = this.travel;
        else if (event.key === 'ArrowLeft') target = this.viewport.scrollLeft - this.viewportWidth * 0.8;
        else if (event.key === 'ArrowRight') target = this.viewport.scrollLeft + this.viewportWidth * 0.8;
        else return;
        event.preventDefault();
        this.scrollLaunch(target);
        return;
      }
      if (!this.pinned) return;
      if (this.looping && ['Home', 'ArrowLeft', 'ArrowUp', 'PageUp'].includes(event.key)) {
        event.preventDefault();
        this.returnToTop();
        return;
      }
      if (this.looping && event.key === 'End') {
        event.preventDefault();
        this.bypassed = true;
        window.scrollTo({ top: this.start + this.scrollSpan + window.innerHeight, behavior: 'instant' });
        return;
      }
      let target;
      if (event.key === 'Home') target = this.start;
      else if (event.key === 'End') target = this.start + this.travel;
      else if (event.key === 'ArrowLeft') target = window.scrollY - this.viewportWidth * 0.7;
      else if (event.key === 'ArrowRight') target = window.scrollY + this.viewportWidth * 0.7;
      else return;
      event.preventDefault();
      window.scrollTo({ top: Math.min(this.start + this.scrollSpan, Math.max(this.start, target)), behavior: 'auto' });
    }

    measureLaunch(previousTravel, notify) {
      const wasPinned = this.launchPinned;
      const previousCount = this.launchStepCount;
      this.launchPinned = !!this.launchRoot?.pager && !this.motionQuery.matches
        && this.dataset.designMode !== 'true' && this.travel > 1;
      this.launchStepCount = this.launchPinned ? Math.ceil(this.travel / Math.max(1, this.viewportWidth * 0.8)) + 1 : 1;
      this.launchScene?.toggleAttribute('data-collage-scroll', this.launchPinned);
      if (this.launchPinned) {
        this.launchScene.style.setProperty('--collage-launch-pages', this.launchStepCount);
        if (!wasPinned) {
          this.viewport.scrollLeft = 0;
        }
        this.setLaunchRange(this.launchRoot.flowTop(this.launchScene), window.innerHeight);
      } else {
        this.launchScene?.style.removeProperty('--collage-launch-pages');
        this.track.style.removeProperty('transform');
        if (wasPinned) this.viewport.scrollLeft = this.offset;
        this.updateLaunchControls();
      }
      this.updateLayer();
      if (notify && (wasPinned !== this.launchPinned || previousCount !== this.launchStepCount || Math.abs(previousTravel - this.travel) > 1)) {
        this.dispatchEvent(new Event('rs7gt:collage-measure', { bubbles: true }));
      }
    }

    setLaunchRange(top, height) {
      this.launchStart = top;
      this.launchSpan = Math.max(1, (this.launchStepCount - 1) * height);
      this.render();
    }

    launchPosition() {
      return Math.round(Math.max(0, Math.min(1, (window.scrollY - this.launchStart) / this.launchSpan)) * (this.launchStepCount - 1));
    }

    selectLaunchStep(index) {
      const step = Math.max(0, Math.min(this.launchStepCount - 1, index));
      const pager = this.launchRoot?.pager;
      const stop = pager?.stops.findIndex(stop => stop.element === this.launchScene && stop.collageStep === step) ?? -1;
      if (stop >= 0) pager.go(stop);
    }

    scrollLaunch(left) {
      this.viewport.scrollTo({
        left: Math.max(0, Math.min(this.travel, left)),
        behavior: this.motionQuery.matches || this.dataset.designMode === 'true' ? 'auto' : 'smooth'
      });
    }

    updateLaunchControls() {
      if (!this.launchMode || !this.viewport) return;
      const max = this.travel;
      const offset = Math.max(0, Math.min(max, this.launchPinned ? this.offset : this.viewport.scrollLeft));
      this.offset = offset;
      this.launchSteps.forEach(button => {
        button.disabled = max <= 1 || (Number(button.dataset.collageStep) < 0 ? offset <= 1 : max - offset <= 1);
      });
      this.launchProgress?.style.setProperty('--collage-progress', String(max > 1 ? offset / max : 0));
    }

    revealBlock(event) {
      if (this.dataset.designMode !== 'true' || !(event.target instanceof Element) || !this.viewport.contains(event.target)) return;
      const photo = event.target.getBoundingClientRect();
      const viewport = this.viewport.getBoundingClientRect();
      this.viewport.scrollTo({
        left: this.viewport.scrollLeft + photo.left - viewport.left - (viewport.width - photo.width) / 2,
        behavior: 'auto'
      });
    }
  });
}
